// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.
import { Location } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { ImageModel } from '../../models/imageData.model';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { AnimationOptions } from 'ngx-lottie';

@Component({
  selector: 'app-lightbox',
  standalone: false,
  templateUrl: './lightbox.component.html',
  styleUrl: './lightbox.component.css'
})
export class LightboxComponent {
  @Input('lightboxImage') lightboxImage: ImageModel | null = null;
  @Input('showNavButtons') showNavButtons = true;
  @Input('disableSwipeNav') disableSwipeNav = false;
  @Input('showShareOption') showShareOption = false;
  @Input('showDeleteIcon') showDeleteIcon = false;
  @Input('showDownloadIcon') showDownloadIcon = false;
  @Input('showAdminDelete') showAdminDelete = false;

  @Output('onClose') onClose = new EventEmitter<string>();
  @Output('onShare') onShare = new EventEmitter<string>();
  @Output('onDelete') onDelete = new EventEmitter<ImageModel>();
  @Output('onAdminDelete') onAdminDelete = new EventEmitter<ImageModel>();

  @Output('navPrev') navPrev = new EventEmitter<any>();
  @Output('navNext') navNext = new EventEmitter<any>();

  @Output('touchStart') touchStart = new EventEmitter<TouchEvent>();
  @Output('touchMove') touchMove = new EventEmitter<TouchEvent>();
  @Output('touchEnd') touchEnd = new EventEmitter<TouchEvent>();
  @Output('keydown') keydown = new EventEmitter<KeyboardEvent>();
  @Output('onLoaded') onLoaded = new EventEmitter<KeyboardEvent>();

  currentIndex = 0;

  lightboxOpen: boolean = false;

  loading = false;
  showDelayedLoading = false;
  private delayTimer: any;

  isLoggedIn$!: Observable<boolean>;
  isAdmin$!: Observable<boolean>;

  animationOptions: AnimationOptions = {
    path: 'assets/lotteFiles/Animated-Heart-Fill.json',
    autoplay: true,
    loop: true
  };

  constructor(
    private authService: AuthService,
    private location: Location,
    private toastService: ToastService
  ) {

  }

  ngOnInit() {
    setTimeout(() => {
      this.isLoggedIn$ = this.authService.isLoggedIn$;
      this.isAdmin$ = this.authService.isAdmin$;
      window.addEventListener('keydown', this.manualKeyHandler);
    });
  }

  ngOnChanges() {
    this.loading = true;

    clearTimeout(this.delayTimer);
    this.delayTimer = setTimeout(() => {
      if (this.loading) {
        this.showDelayedLoading = true;
      }
    }, 200);
  }

  ngOnDestroy() {
    document.body.style.overflow = 'auto';
    window.removeEventListener('keydown', this.manualKeyHandler);
  }

  downloadImage() {
    if (!this.lightboxImage?.imageUrl) return;
    const link = document.createElement('a');
    link.href = this.lightboxImage.imageUrl;
    link.download = this.lightboxImage.fileName || 'downloaded-image.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  onPrevClick() {
    this.navPrev.emit();
  }

  onNextClick() {
    this.navNext.emit();
  }

  shareImage(imageId: string | null | undefined) {
    if (!imageId) {
      return;
    }

    this.onShare.emit(imageId);
  }

  onImageLoaded() {
    this.loading = false;
    this.showDelayedLoading = false;
    clearTimeout(this.delayTimer); this.onLoaded.emit();
  }

  onImageError() {
    this.loading = false;
    this.showDelayedLoading = false;
    clearTimeout(this.delayTimer); this.onLoaded.emit();
  }

  deleteImageByAdmin(image: ImageModel | null) {
    if (image)
      this.onAdminDelete.emit(image);
  }

  deleteImage(image: ImageModel | null) {
    if (image)
      this.onDelete.emit(image);
  }

  onCloseClick() {
    this.onClose.emit('closed');
  }

  handleKeydown(event: KeyboardEvent) {
    if (this.lightboxOpen) {
      if (event.key === 'ArrowRight') {
        this.navNext.emit();
      } else if (event.key === 'ArrowLeft') {
        this.navPrev.emit();
      } else if (event.key === 'Escape') {
        this.onClose.emit();
      }
    }
  }

  //Swipe Actions
  private touchStartX = 0;
  private touchEndX = 0;
  private touchPoints = 0;

  onTouchStart(event: TouchEvent) {
    this.touchStartX = event.touches[0].clientX;
    this.touchPoints = event.touches.length;
  }

  onTouchMove(event: TouchEvent) {
    this.touchEndX = event.touches[0].clientX;
  }

  onTouchEnd(event: TouchEvent) {
    const threshold = 50;
    const deltaX = this.touchStartX - this.touchEndX;

    const touchedElement = event.target as HTMLElement;

    if (touchedElement.closest('button')) {
      return;
    }

    if (this.touchPoints > 1) {
      return;
    }

    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0) {
        this.navNext.emit();
      } else {
        this.navPrev.emit();
      }
    }
  }

  manualKeyHandler = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') this.onPrevClick();
    else if (event.key === 'ArrowRight') this.onNextClick();
    else if (event.key === 'Escape') this.onCloseClick();
  };
}
